import Link from 'next/link';
import { NextSeo } from 'next-seo';

import { DocsArticleLayout, DocsCallout, DocsTable } from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_URL,
  CONNECTOR_HOSTS,
  CONNECTOR_STATUS_CHECKED,
  ConnectorCreditsTable,
  ConnectorFlowDiagram,
  ConnectorHostGrid,
  ConsentExplainer,
} from '@/components/modules/Docs/connectors';
import PageLink from '@/utils/PageLink';

const tableOfContents = [
  { id: 'how-it-works', title: '1. How it works' },
  { id: 'choose-your-app', title: '2. Choose your AI app' },
  { id: 'connect', title: '3. Connecting your account' },
  { id: 'experts-and-sources', title: '4. Experts and sources' },
  { id: 'credits', title: '5. Message credits' },
  { id: 'privacy', title: '6. Data and privacy' },
  { id: 'manage', title: '7. Manage your connections' },
  { id: 'developer-reference', title: '8. For developers & agents' },
];

export default function ExternalExpertsGuide() {
  return (
    <>
      <NextSeo
        title="Chat.co experts in your AI apps"
        description="Use your Chat.co experts from Claude, ChatGPT, Cursor, Codex, Grok and more: availability per app, OAuth sign-in, cited answers, credits and connected applications."
        canonical={`https://chat.co${PageLink.docsExternalExperts}`}
        openGraph={{
          url: `https://chat.co${PageLink.docsExternalExperts}`,
          title: 'Chat.co experts in your AI apps | Chat.co Docs',
          description:
            'Availability per AI app, OAuth sign-in, cited answers, credits and connected applications.',
          type: 'article',
          article: {
            publishedTime: '2026-09-30T00:00:00Z',
            modifiedTime: '2026-09-30T00:00:00Z',
            section: 'Integrations',
            tags: ['experts', 'connector', 'mcp', 'claude', 'chatgpt', 'cursor', 'codex', 'grok'],
          },
        }}
      />
      <DocsArticleLayout
        category="Experts in AI apps"
        categoryColor="text-orange-700"
        title="Your Chat.co experts in your AI apps"
        description="Ask the experts in your Chat.co account from Claude, ChatGPT, Cursor, Codex, Grok and other AI apps, and follow every answer back to its sources."
        readingTime="6 min read"
        lastUpdated="September 2026"
        tableOfContents={tableOfContents}
        nextArticle={{ title: `${CONNECTOR_HOSTS[0].name} setup`, href: CONNECTOR_HOSTS[0].href }}
        keyTakeaways={[
          'One server for every app: you sign in to your own Chat.co account and pick a workspace.',
          'Your existing expert access applies; the app gets no access you don’t already have.',
          'Your questions, and the answers and sources you may see, pass to the AI app’s provider.',
          'Answers include citations, and sources open only with your current Chat.co access.',
          'Expert answers use their normal rate; standalone searches cost 0.5 credit; the rest is free.',
          'Disconnect any app from Connected applications in Chat.co.',
        ]}>
        <section id="how-it-works" className="scroll-mt-28">
          <h2>1. How it works</h2>
          <p>
            Chat.co runs one connector server at <code>{CHATCO_MCP_URL}</code>, using the Model
            Context Protocol (MCP) that many AI apps support. The app connects to it, you approve
            the connection in Chat.co, and from then on the app can ask the experts you already use
            in Chat.co.
          </p>
          <ConnectorFlowDiagram />
        </section>

        <section id="choose-your-app" className="scroll-mt-28">
          <h2>2. Choose your AI app</h2>
          <p>
            Each app has its own setup guide. Availability differs: a directory listing is only
            described as published once the vendor has published it, and a status we could not
            confirm is described as unconfirmed. Status reviewed {CONNECTOR_STATUS_CHECKED}.
          </p>
          <ConnectorHostGrid />
          <DocsTable
            headers={['App', 'Status', 'How to connect']}
            rows={CONNECTOR_HOSTS.map((host) => [
              <Link
                key={host.slug}
                href={host.href}
                className="font-medium text-primary-700 underline">
                {host.name}
              </Link>,
              host.status,
              host.howToConnect,
            ])}
          />
          <DocsCallout variant="note" title="Other MCP apps">
            Other apps that support remote Streamable HTTP MCP servers with OAuth may work with the
            same URL. We haven&apos;t tested them, so we can&apos;t promise they will.
          </DocsCallout>
        </section>

        <section id="connect" className="scroll-mt-28">
          <h2>3. Connecting your account</h2>
          <p>
            Whichever app you use, you add Chat.co from that app&apos;s connection settings and sign
            in to your own Chat.co account. You never need an API key or another person&apos;s
            login.
          </p>
          <ConsentExplainer />
        </section>

        <section id="experts-and-sources" className="scroll-mt-28">
          <h2>4. Experts and sources</h2>
          <p>
            If you can chat with an expert in Chat.co, you can ask that expert through the
            connector. Follow-up questions stay in the same expert conversation. Your current
            account, workspace and licence permissions apply to every request.
          </p>
          <p>
            Document-based answers include citations and links to the supporting documents. You
            don&apos;t need permission to manage an expert&apos;s knowledge to open the evidence for
            an answer you were allowed to receive. Private documents require sign-in and current
            access; deleted or unavailable sources may no longer open.
          </p>
          <p>
            Searching documents directly is a separate capability. It is available only when your
            account may retrieve that expert&apos;s knowledge, and the expert list shows whether you
            can.
          </p>
        </section>

        <section id="credits" className="scroll-mt-28">
          <h2>5. Message credits</h2>
          <ConnectorCreditsTable />
          <p>Manage your allocation through your usual Chat.co account process.</p>
        </section>

        <section id="privacy" className="scroll-mt-28">
          <h2>6. Data and privacy</h2>
          <p>
            A connected app can send your questions to Chat.co and receive the authorized answers,
            source excerpts, citations and document links, plus any assessment or file you ask an
            expert to produce. Where requested during sign-in, it also receives your account
            identifier, email address and email-verification status. It cannot manage or delete your
            chatbots through this connector.
          </p>
          <p>
            The app&apos;s own terms and privacy policy govern the information it receives.
            Disconnecting stops future access but cannot remove content already delivered to that
            app. Chat.co&apos;s <Link href={PageLink.privacy}>Privacy Policy</Link> and{' '}
            <Link href={PageLink.terms}>Terms</Link> apply to your use of Chat.co.
          </p>
        </section>

        <section id="manage" className="scroll-mt-28">
          <h2>7. Manage your connections</h2>
          <p>
            Open <Link href="/connect">Connected applications</Link> to review each app and its
            workspace, or disconnect it. To reconnect, start again from the app&apos;s connection
            settings.
          </p>
          <p>
            If an expert is missing, check that you chose the right workspace and still have access
            in Chat.co. If a private document doesn&apos;t open, sign in with the account that
            received the answer. Each setup guide lists the errors specific to its app.
          </p>
        </section>

        <section id="developer-reference" className="scroll-mt-28">
          <h2>8. For developers &amp; agents</h2>
          <AgentProtocolReference />
          <p className="text-sm text-slate-500">
            Claude, ChatGPT, OpenAI, Codex, Cursor, Grok and xAI are trademarks of their respective
            owners. Their names and logos appear only to identify compatible apps and do not imply
            endorsement.
          </p>
        </section>
      </DocsArticleLayout>
    </>
  );
}
